import { useEffect, useRef, useState } from 'react';
import { team, type Fan, type Receipt } from '@clutch/core';
import { useClient } from '../client';
import { navigate } from '../router';
import { matchupTricodes, receiptLayout } from '../receipt/layout';
import { canvasToBlob, renderReceipt, shareOrDownload } from '../receipt/render';
import { teamAccent } from '../ui/color';

export function ReceiptScreen({ roomId, fan }: { roomId: string; fan: Fan }) {
  const client = useClient();
  const [receipt, setReceipt] = useState<Receipt>();
  const [error, setError] = useState<string>();
  const [png, setPng] = useState<string>();
  const [shareState, setShareState] = useState<string>();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let alive = true;
    let tries = 0;
    const load = () =>
      client
        .getReceipt(roomId, fan.fanId)
        .then((r) => alive && setReceipt(r))
        .catch(() => {
          if (!alive) return;
          if (++tries < 8) setTimeout(load, 1200);
          else setError('No receipt yet — it’s minted at the final buzzer for fans who locked a call.');
        });
    load();
    return () => {
      alive = false;
    };
  }, [client, roomId, fan.fanId]);

  useEffect(() => {
    if (!receipt) return;
    let alive = true;
    const t = matchupTricodes(receipt.matchup);
    const homeIsChoice = t && receipt.choiceLabel === teamName(t.home);
    const choiceColor = t ? teamAccent(homeIsChoice ? t.home : t.away) : undefined;
    renderReceipt(receiptLayout(receipt, { choiceColor })).then((c) => {
      if (!alive) return;
      canvasRef.current = c;
      setPng(c.toDataURL('image/png'));
    });
    return () => {
      alive = false;
    };
  }, [receipt]);

  const share = async () => {
    if (!canvasRef.current || !receipt) return;
    const blob = await canvasToBlob(canvasRef.current);
    const r = await shareOrDownload(blob, `clutch-${receipt.gameId}-${receipt.displayName}.png`, receipt.question);
    setShareState(r === 'downloaded' ? 'Saved to your downloads' : r === 'shared' ? 'Shared' : undefined);
  };

  return (
    <main className="receipt">
      <header className="receipt__top">
        <button className="linkbtn" onClick={() => navigate(`/room/${encodeURIComponent(roomId)}`)}>
          ← Room
        </button>
        <span className="wordmark wordmark--sm">CLUTCH</span>
      </header>
      {error && <p className="alert">{error}</p>}
      {!error && !png && <div className="receipt__skeleton" aria-busy="true" />}
      {png && (
        <>
          <img className="receipt__img" src={png} alt={receipt ? altText(receipt) : 'Receipt'} data-testid="receipt-img" />
          <button className="btn btn--primary btn--block receipt__share" onClick={share} data-testid="share">
            Share receipt
          </button>
          {shareState && <p className="receipt__note">{shareState}</p>}
        </>
      )}
    </main>
  );
}

function teamName(code: string) {
  return team(code).name;
}

function altText(r: Receipt): string {
  return `${r.displayName} called ${r.choiceLabel} — ${r.result === 'correct' ? 'called it' : r.result === 'incorrect' ? 'bold call' : 'no result'}. Final ${r.finalScore}.`;
}
